import React, { useState } from 'react'
import { motion, useMotionValueEvent, useScroll } from 'framer-motion'
import { FaWhatsapp } from 'react-icons/fa'
import { whatsAppUrl } from 'utils/business-info'

// Sticky Book + WhatsApp bar, shared by every service hub and its sub-pages.
//
// It stays hidden until the visitor scrolls past the hero (100px), where the
// booking widget is already in view, and slides up after that.
//
// Props:
//  - onBook: what "Book" does. The handyman pages open the job picker; the
//    aircon and helper pages scroll back to the booking card.
//  - whatsAppText: pre-filled WhatsApp message.
//  - whatsAppHref: overrides the number (aircon's BottomButtons picks one from
//    app settings). Without it the number comes from utils/business-info, so
//    the server-rendered HTML is correct. The old bars linked to wa.me/false
//    until app settings loaded.
//  - secondary: a node that replaces the WhatsApp link (aircon self-help chat).
//  - spacer: reserve room at the end of the page so the bar never covers the
//    footer on mobile. Pass spacer={false} when the bar is rendered mid-page
//    (HandymanBookingPanel, BottomButtons); that page adds the spacer at the very bottom itself.
export const StickyCta = ({ onBook, bookLabel = 'Book Now', whatsAppText, whatsAppHref, secondary, spacer = true }) => {
    const [ isVisible, setIsVisible ] = useState(false)
    const { scrollY } = useScroll()

    useMotionValueEvent(scrollY, 'change', (latest) => {
        const next = latest > 100
        if (next !== isVisible) setIsVisible(next)
    })

    const baseHref = whatsAppHref || whatsAppUrl
    const waHref = whatsAppText
        ? `${baseHref}?text=${encodeURIComponent(whatsAppText)}`
        : baseHref

    return (
        <>
        {spacer && <div aria-hidden="true" className="h-20 md:hidden" />}
        <motion.div
            className="fixed inset-x-0 bottom-0 z-20 bg-white/95 backdrop-blur border-t border-gray-200 px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:bg-transparent md:border-0 md:backdrop-blur-none md:pb-4 md:pointer-events-none"
            initial={{ y: '120%' }}
            animate={{ y: isVisible ? '0%' : '120%' }}
            transition={{ type: 'spring', stiffness: 120, damping: 20 }}
            aria-hidden={!isVisible}
        >
            <div className="max-w-md mx-auto flex gap-3 md:justify-center md:pointer-events-auto">
                <button
                    type="button"
                    onClick={onBook}
                    tabIndex={isVisible ? 0 : -1}
                    className="flex-1 md:flex-none flex items-center justify-center min-h-12 px-8 rounded-full bg-linear-to-tr from-orange-600 to-amber-400 text-white font-bold shadow-lg"
                >
                    {bookLabel}
                </button>
                {secondary || (
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        tabIndex={isVisible ? 0 : -1}
                        aria-label="Chat with StringsSG on WhatsApp"
                        className={stickyCtaSecondaryClass}
                    >
                        <FaWhatsapp className="text-xl" aria-hidden="true" />
                        WhatsApp
                    </a>
                )}
            </div>
        </motion.div>
        </>
    )
}

// Class for the green secondary button, exported so a `secondary` node can
// match the WhatsApp button.
export const stickyCtaSecondaryClass = 'flex-1 md:flex-none flex items-center justify-center gap-2 min-h-12 px-6 rounded-full bg-linear-to-tl from-green-700 to-green-500 text-white font-bold shadow-lg'
